<template>
  <v-app id="app" :class="{ 'mobile-ios-safari': mobileIosSafari, mobile: $vuetify.display.mobile }">
    <template v-if="initialized">
      <header-desktop v-if="showDesktopHeader" />
      <nav-more-menu />
      <nav-inbox v-if="isLoggedIn" />
      <pull-to-refresh
        v-if="loggedInAndRedirected"
        :class="{
          'mobile-post-background':
            $vuetify.display.mobile && [$routes.POST, $routes.ARTICLE, $routes.PROFILE_EDIT, $routes.MEMBER_DIRECTORY].includes($route.name)
        }"
        class="fill-height"
        @pull-to-refresh="$eventBus.$emit('pull-to-refresh')"
      >
        <the-layout />
      </pull-to-refresh>
      <the-layout v-else />
      <emails-modal v-if="canShowEmailsModal && emailsModalDisplayed" @close="emailsModalDisplayed = false" />
      <cookies-banner v-if="!webViewMode" />
    </template>
    <events-hub @log-out="logOutUser" />
    <snackbars-hub />
    <onboarding-media v-if="onboardingMediaDisplayed" fullscreen @close="onboardingMediaDisplayed = false" />
  </v-app>
</template>

<script>
import { mapActions, mapGetters } from "pinia"

import { useConfigurationStore } from "@/store/configuration.js"
import { useAppStateStore } from "@/store/appState.js"
import { useUserSessionStore } from "@/store/userSession.js"
import { useGroupsStore } from "@/store/groups.js"
import { useMessagingStore } from "@/store/messaging.js"
import { useCoursesStore } from "@/store/courses.js"

import { differenceInWeeks } from "date-fns"

import TheLayout from "@/components/TheLayout.vue"

import HeaderDesktop from "@/components/navs/HeaderDesktop.vue"
import NavMoreMenu from "@/components/navs/NavMoreMenu.vue"
import NavInbox from "@/components/navs/NavInbox.vue"
import EmailsModal from "@/components/EmailsModal.vue"
import EventsHub from "@/components/EventsHub.vue"
import SnackbarsHub from "@/components/SnackbarsHub.vue"
import OnboardingMedia from "@/components/onboarding/OnboardingMedia.vue"

import { sendAnalytics } from "@/services/analytics.js"
import * as styles from "@/services/styles.js"
import * as environment from "@/services/environment.js"
import * as pushNotifications from "@/services/pushNotifications.js"
import * as pubNub from "@/services/pubNub.js"
import * as sentryReporter from "@/services/sentryReporter.js"

import { usePubNubSubscriptions } from "@/composables/usePubNubSubscriptions.js"

import PullToRefresh from "@/components/PullToRefresh.vue"
import CookiesBanner from "@/components/CookiesBanner.vue"

import { getRouterState } from "@/router"

const appRefreshTimeoutMinValueInSeconds = 60
const registrationSnippetsRefreshTimeoutMinValueInSeconds = 60 * 30
const appRefreshIntervalInSeconds = 60 * 5
const pubNubInactivityTimeInSeconds = 60 * 10
const recheckAppVersionIntervalInSeconds = 60 * 30

export default {
  components: {
    TheLayout,
    HeaderDesktop,
    NavMoreMenu,
    NavInbox,
    EventsHub,
    SnackbarsHub,
    EmailsModal,
    PullToRefresh,
    CookiesBanner,
    OnboardingMedia
  },
  setup() {
    const { subscribeToChannel, unsubscribeFromChannel, unsubscribeFromAllChannels, suspendChannelSubscriptions, resumeChannelSubscriptions } =
      usePubNubSubscriptions()

    return {
      subscribeToChannel,
      unsubscribeFromChannel,
      unsubscribeFromAllChannels,
      suspendChannelSubscriptions,
      resumeChannelSubscriptions
    }
  },
  data() {
    return {
      initialized: false,
      mobileIosSafari: environment.isMobileIosSafari(),
      lastRefreshTimestamp: new Date().getTime(),
      lastRegistrationSnippetsRefreshTimestamp: new Date().getTime(),
      lastAppVersionRecheckTimestamp: new Date().getTime(),
      refreshIntervalHandler: null,
      inactivityTimeoutHandler: null,
      emailsModalDisplayed: false,
      onboardingMediaDisplayed: false
    }
  },
  computed: {
    ...mapGetters(useConfigurationStore, ["configuration"]),
    ...mapGetters(useUserSessionStore, [
      "user",
      "session",
      "isLoggedIn",
      "ownedPaywallPlan",
      "messagingUpdatesChannel",
      "marketingPreferences",
      "privacyPolicyOutdated",
      "termsAndConditionsOutdated",
      "walkthroughCompleted"
    ]),
    ...mapGetters(useAppStateStore, [
      "enablePushNotifications",
      "lastPushNotificationsAnalyticsUpdate",
      "redirectingAfterLogIn",
      "onboardingModeOrRedirection",
      "webViewMode",
      "onboardingRedirection"
    ]),
    subscriptionRequired() {
      return this.configuration.isPremium && !this.ownedPaywallPlan
    },
    canShowEmailsModal() {
      return (
        !this.privacyPolicyOutdated &&
        !this.termsAndConditionsOutdated &&
        !this.onboardingModeOrRedirection &&
        !this.webViewMode &&
        this.$route.name !== this.$routes.WALKTHROUGH
      )
    },
    loggedInAndRedirected() {
      return this.isLoggedIn && !this.redirectingAfterLogIn
    },
    showDesktopHeader() {
      if (this.$vuetify.display.mobile) {
        return false
      }

      if (this.$route.name === this.$routes.POST) {
        return true
      }

      if (
        (this.$route.name === this.$routes.PROFILE_EDIT && !this.user?.onboarding_completed) ||
        this.onboardingRedirection ||
        (this.$route.name === this.$routes.WALKTHROUGH && !this.walkthroughCompleted)
      ) {
        return false
      }

      return this.loggedInAndRedirected
    }
  },
  watch: {
    $route(newVal) {
      if (this.$routerState.isRoot) {
        this.checkShowEmailModal()
      }
      if (environment.isMobileIosSafari() && newVal.name === this.$routes.MESSAGES) {
        document.documentElement.style.overflowY = "hidden"
      } else {
        document.documentElement.style.overflowY = null
      }
    },
    session() {
      this.updatePushNotificationsToken()

      this.fetchPaywallIfLoggedInAndNotOnPaywall()
    },
    user(newValue, oldValue) {
      sentryReporter.setUserId(newValue?.id)
      const afterLogIn = !oldValue && newValue
      const afterOnboarding = oldValue?.onboarding_completed === false && newValue?.onboarding_completed

      if (afterLogIn) {
        this.fetchLimitsIfLoggedIn()
        this.refreshGroupsAndRedirectAfterLogIn()
        this.subscribeToChannelIfLoggedIn()
      }

      if (afterLogIn || afterOnboarding) {
        this.updatePushNotificationsStatus()
      }
    }
  },
  async created() {
    this.setOnboardingMediaDisplayed()
    styles.setAppStyles()

    window.addEventListener("visibilitychange", this.onVisibilityChange)

    await this.refreshSessionIfNeeded()
    await Promise.all([this.refreshAllGroupsIfLoggedIn(), this.fetchLimitsIfLoggedIn()])

    sentryReporter.setUserId(this.user?.id)
    this.updatePushNotificationsStatus()
    this.subscribeToChannelIfLoggedIn()
    this.updatePushNotificationsToken()
    this.validateNotificationsPermissions()
    this.watchForPermissionsChanges()
    this.fetchPaywallIfLoggedInAndNotOnPaywall()

    this.refreshIntervalHandler = setInterval(this.reloadConfigs, appRefreshIntervalInSeconds * 1000)
    this.initialized = true
  },
  beforeUnmount() {
    this.unsubscribeFromChannel(this.messagingUpdatesChannel)

    window.removeEventListener("visibilitychange", this.onVisibilityChange)

    clearInterval(this.refreshIntervalHandler)
  },
  mounted() {
    this.checkShowEmailModal()
    environment.notifyIfAppVersionOutdated()
  },
  methods: {
    ...mapActions(useUserSessionStore, ["resetCounters", "refreshSession", "logOut", "fetchPaywallData", "clearSession"]),
    ...mapActions(useGroupsStore, ["clearGroups", "refreshAllGroups"]),
    ...mapActions(useConfigurationStore, ["fetchConfiguration", "fetchLimits"]),
    ...mapActions(useMessagingStore, ["clearSavedMessages", "clearChats"]),
    ...mapActions(useCoursesStore, ["clearCourseAndLessons"]),
    ...mapActions(useAppStateStore, ["setRedirectingAfterLogin", "setLocalAvatarUrl", "setPushNotifications", "clearState"]),
    async logOutUser({ dontCallApi } = {}) {
      this.unsubscribeFromChannel(this.messagingUpdatesChannel)
      this.setLocalAvatarUrl(null)

      try {
        await sendAnalytics()
      } catch (err) {
        this.$reportError(err)
      } finally {
        if (!dontCallApi) {
          await this.logOut()
        } else {
          this.clearSession()
        }
        await this.$router.push({ name: this.$routes.LOGIN })
        this.clearGroups()
        this.clearState()
        this.clearChats()
        this.clearSavedMessages()
        this.clearCourseAndLessons()
        this.unsubscribeFromAllChannels()
        pubNub.destroy()
      }
    },
    shouldRefreshSession() {
      return this.session?.expires && differenceInWeeks(new Date(this.session.expires), new Date()) < 2
    },
    async refreshSessionIfNeeded() {
      if (this.shouldRefreshSession()) {
        try {
          await this.refreshSession()
        } catch {
          this.logOutUser({ dontCallApi: true })
        }
      }
    },
    setOnboardingMediaDisplayed() {
      this.onboardingMediaDisplayed =
        this.$vuetify.display.mobile && !this.isLoggedIn && (this.configuration.onboardingVideoUrl || this.configuration.onboardingImage?.length)
    },
    updatePushNotificationsToken() {
      if (this.session) {
        pushNotifications.initializePushNotifications()
      } else {
        pushNotifications.deletePushNotificationMessagingToken()
      }
    },
    async refreshAllGroupsIfLoggedIn() {
      if (this.session) {
        await this.refreshAllGroups()
      }
    },
    async fetchLimitsIfLoggedIn() {
      if (this.session) {
        await this.fetchLimits()
      }
    },
    async fetchPaywallIfLoggedInAndNotOnPaywall() {
      if (this.session && this.$route.name !== this.$routes.PAYWALL) {
        await this.fetchPaywallData()
      }
    },
    subscribeToChannelIfLoggedIn() {
      if (this.isLoggedIn && this.configuration.pubnub) {
        if (this.messagingUpdatesChannel) {
          this.subscribeToChannel(this.messagingUpdatesChannel)
        }
        this.subscribeToChannel(this.configuration.pubnub.channel_control_v2)
      }
    },
    onVisibilityChange() {
      clearTimeout(this.inactivityTimeoutHandler)

      if (document.hidden) {
        this.inactivityTimeoutHandler = setTimeout(() => this.suspendChannelSubscriptions(), pubNubInactivityTimeInSeconds * 1000)
      } else {
        this.reloadConfigs({ onVisibilityChange: true })
        this.resumeChannelSubscriptions()
      }
    },
    checkShowEmailModal() {
      this.emailsModalDisplayed = this.configuration.emailNotificationsEnabled && this.marketingPreferences.global_opt_in_can_ask_modal
    },
    async reloadConfigs({ onVisibilityChange } = {}) {
      if (this.isLoggedIn && !onVisibilityChange) {
        await this.refreshSessionIfNeeded()
      }
      const currentTimestamp = new Date().getTime()

      if (!document.hidden && currentTimestamp - this.lastRefreshTimestamp > appRefreshTimeoutMinValueInSeconds * 1000) {
        const includeRegistrationSnippets =
          currentTimestamp - this.lastRegistrationSnippetsRefreshTimestamp > registrationSnippetsRefreshTimeoutMinValueInSeconds * 1000

        try {
          if (this.isLoggedIn) {
            await Promise.all([
              this.refreshAllGroups(),
              this.fetchConfiguration({ includeRegistrationSnippets }),
              this.fetchLimits(),
              this.resetCounters(),
              this.fetchPaywallData()
            ])
          } else {
            await this.fetchConfiguration({ includeRegistrationSnippets })
          }

          styles.setAppStyles()
          this.lastRefreshTimestamp = currentTimestamp

          if (includeRegistrationSnippets) {
            this.lastRegistrationSnippetsRefreshTimestamp = currentTimestamp
          }

          if (currentTimestamp - this.lastAppVersionRecheckTimestamp > recheckAppVersionIntervalInSeconds * 1000) {
            environment.notifyIfAppVersionOutdated()
            this.lastAppVersionRecheckTimestamp = currentTimestamp
          }
        } catch (err) {
          // Do not clear session when there is a network error or server is down
          if (err && err.response && err.response.status !== 500) {
            this.logOutUser()
          }
        }
      }
    },
    updatePushNotificationsStatus() {
      if (!this.session || !environment.supportsPushNotifications()) {
        return
      }

      const notificationsEnabled = Notification.permission === "granted"

      if (this.lastPushNotificationsAnalyticsUpdate === null || notificationsEnabled !== this.lastPushNotificationsAnalyticsUpdate) {
        pushNotifications.updatePushNotificationEnabledAnalytics(notificationsEnabled)
      }
    },
    async watchForPermissionsChanges() {
      if (navigator.permissions) {
        try {
          const notificationPerm = await navigator.permissions.query({ name: "notifications" })

          notificationPerm.onchange = () => {
            this.$eventBus.$emit("notification-permissions-change")
            const granted = Notification.permission === "granted"
            this.setPushNotifications(granted)
            this.updatePushNotificationsStatus()
            if (granted) {
              pushNotifications.initializePushNotifications()
            }
          }
        } catch (err) {
          // Need to catch because Safari 16 throws an error
          // https://disciple.kanbanize.com/ctrl_board/58/cards/53007/details/
          if (err.name !== "NotSupportedError") {
            throw err
          }
        }
      }
    },
    validateNotificationsPermissions() {
      if (!environment.supportsPushNotifications()) {
        return
      }
      if (Notification.permission !== "granted" && this.enablePushNotifications) {
        this.setPushNotifications(false)
        this.$eventBus.$emit("notification-permissions-change")
      }
    },
    async refreshGroupsAndRedirectAfterLogIn() {
      try {
        this.setRedirectingAfterLogin(true)

        await this.refreshAllGroupsIfLoggedIn()
        await this.$router.push(
          !this.user.onboarding_completed ? { name: this.$routes.PROFILE_EDIT } : getRouterState().authRedirect || { name: this.$routes.ROOT }
        )
      } catch (err) {
        this.$reportError(err)
      } finally {
        this.setRedirectingAfterLogin(false)
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/app-styles.scss";

#app.mobile {
  min-height: 100%;

  &.mobile-ios-safari {
    min-height: -webkit-fill-available;
  }

  .v-application--wrap {
    min-height: 100%;
  }
}

.mobile-post-background {
  background-color: var(--post-background) !important;

  :deep(.v-main) {
    background-color: var(--post-background) !important;
  }
}
</style>
